import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  writeBatch,
  getDoc,
} from 'firebase/firestore';
import { deleteUser as firebaseDeleteUser } from 'firebase/auth';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase/config';

export interface DataDeletionResult {
  success: boolean;
  message: string;
  deletedCollections: string[];
  errors?: string[];
}

/**
 * Service to handle complete user data deletion
 * This deletes all user-related data from Firestore collections
 */
export const dataDeletionService = {
  /**
   * Delete all user data from the system
   * This is a comprehensive deletion that removes:
   * - User profile
   * - Runs/activities
   * - Goals and progress
   * - Points and transactions
   * - Achievements
   * - Badges
   * - Personal records
   * - Team memberships
   * - Avatar data
   * - Challenge/mission progress
   * - Prize redemptions
   * - Daily activities
   * - Firebase Auth account
   */
  async deleteUserData(userId: string, userRole: 'coach' | 'athlete' | 'headCoach'): Promise<DataDeletionResult> {
    const deletedCollections: string[] = [];
    const errors: string[] = [];
    const batchSize = 500; // Firestore batch limit

    try {
      // Check if user is a coach or head coach with teams
      if (userRole === 'coach' || userRole === 'headCoach') {
        // Check for teams where user is head coach
        const headCoachTeamsQuery = query(
          collection(db, 'teams'),
          where('headCoachId', '==', userId)
        );
        const headCoachTeamsSnapshot = await getDocs(headCoachTeamsQuery);
        
        // Also check for legacy coachId (backward compatibility)
        const coachTeamsQuery = query(
          collection(db, 'teams'),
          where('coachId', '==', userId)
        );
        const coachTeamsSnapshot = await getDocs(coachTeamsQuery);
        
        if (!headCoachTeamsSnapshot.empty || !coachTeamsSnapshot.empty) {
          return {
            success: false,
            message: 'Cannot delete account: You are the coach of one or more teams. Please transfer team ownership or delete your teams first.',
            deletedCollections: [],
            errors: ['Coach owns teams that must be handled first'],
          };
        }
      }

      // Delete in batches to handle Firestore limits
      const collectionsToDelete = [
        { collection: 'runs', field: 'athleteId' },
        { collection: 'goals', field: 'athleteId' },
        { collection: 'goalProgress', field: 'athleteId' },
        { collection: 'dailyActivities', field: 'userId' },
        { collection: 'personalRecords', field: 'userId' },
        { collection: 'userBadges', field: 'userId' },
        { collection: 'achievements', field: 'userId' },
        { collection: 'userChallengeProgress', field: 'userId' },
        { collection: 'userMissionProgress', field: 'userId' },
        { collection: 'prizeRedemptions', field: 'userId' },
        { collection: 'pointsTransactions', field: 'userId' },
        { collection: 'teamMembers', field: 'athleteId' },
        { collection: 'workoutCompletions', field: 'athleteId' },
      ];

      // Delete user data from each collection
      for (const { collection: collectionName, field } of collectionsToDelete) {
        try {
          const q = query(
            collection(db, collectionName),
            where(field, '==', userId)
          );
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
            // Delete in batches
            const docs = snapshot.docs;
            for (let i = 0; i < docs.length; i += batchSize) {
              const batch = writeBatch(db);
              const batchDocs = docs.slice(i, i + batchSize);
              
              batchDocs.forEach((docSnapshot) => {
                batch.delete(docSnapshot.ref);
              });
              
              await batch.commit();
            }
            deletedCollections.push(collectionName);
          }
        } catch (error: any) {
          console.error(`Error deleting from ${collectionName}:`, error);
          errors.push(`${collectionName}: ${error.message}`);
        }
      }

      // Delete points document
      try {
        const pointsQuery = query(
          collection(db, 'points'),
          where('userId', '==', userId)
        );
        const pointsSnapshot = await getDocs(pointsQuery);
        
        if (!pointsSnapshot.empty) {
          const batch = writeBatch(db);
          pointsSnapshot.docs.forEach((docSnapshot) => {
            batch.delete(docSnapshot.ref);
          });
          await batch.commit();
          deletedCollections.push('points');
        }
      } catch (error: any) {
        console.error('Error deleting points:', error);
        errors.push(`points: ${error.message}`);
      }

      // Delete user avatar data
      try {
        const avatarQuery = query(
          collection(db, 'userAvatars'),
          where('userId', '==', userId)
        );
        const avatarSnapshot = await getDocs(avatarQuery);
        
        if (!avatarSnapshot.empty) {
          const batch = writeBatch(db);
          avatarSnapshot.docs.forEach((docSnapshot) => {
            batch.delete(docSnapshot.ref);
          });
          await batch.commit();
          deletedCollections.push('userAvatars');
        }
      } catch (error: any) {
        console.error('Error deleting user avatars:', error);
        // Not critical, continue
      }

      // Delete user inventory
      // Note: Collection is 'userInventories' and uses composite key: teamId_userId
      try {
        // Get all team memberships to find which teams the user belongs to
        const teamMembersQuery = query(
          collection(db, 'teamMembers'),
          where('athleteId', '==', userId)
        );
        const teamMembersSnapshot = await getDocs(teamMembersQuery);
        const teamIds = teamMembersSnapshot.docs.map(doc => doc.data().teamId);
        
        // Also check if user is a coach or head coach of any teams
        if (userRole === 'coach' || userRole === 'headCoach') {
          // Check for head coach teams
          const headCoachTeamsQuery = query(
            collection(db, 'teams'),
            where('headCoachId', '==', userId)
          );
          const headCoachTeamsSnapshot = await getDocs(headCoachTeamsQuery);
          teamIds.push(...headCoachTeamsSnapshot.docs.map(doc => doc.id));
          
          // Also check for legacy coachId (backward compatibility)
          const coachTeamsQuery = query(
            collection(db, 'teams'),
            where('coachId', '==', userId)
          );
          const coachTeamsSnapshot = await getDocs(coachTeamsQuery);
          teamIds.push(...coachTeamsSnapshot.docs.map(doc => doc.id));
        }
        
        // Delete inventory for each team
        const uniqueTeamIds = Array.from(new Set(teamIds));
        const deletePromises = uniqueTeamIds.map(async (teamId) => {
          try {
            const inventoryRef = doc(db, 'userInventories', `${teamId}_${userId}`);
            const inventoryDoc = await getDoc(inventoryRef);
            if (inventoryDoc.exists()) {
              await deleteDoc(inventoryRef);
            }
          } catch (error: any) {
            // Ignore individual errors, continue with other teams
            console.log(`Could not delete inventory for team ${teamId}:`, error.message);
          }
        });
        
        await Promise.all(deletePromises);
        if (uniqueTeamIds.length > 0) {
          deletedCollections.push('userInventories');
        }
      } catch (error: any) {
        console.error('Error deleting user inventory:', error);
        // Not critical, continue
      }

      // Delete user document
      try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          await deleteDoc(userRef);
          deletedCollections.push('users');
        }
      } catch (error: any) {
        console.error('Error deleting user document:', error);
        errors.push(`users: ${error.message}`);
      }

      // Delete user's Firebase Storage files (avatars, profile pictures, etc.)
      try {
        const userStorageRef = ref(storage, `users/${userId}`);
        const fileList = await listAll(userStorageRef);
        
        if (fileList.items.length > 0) {
          const deletePromises = fileList.items.map((itemRef) => deleteObject(itemRef));
          await Promise.all(deletePromises);
          deletedCollections.push('storage');
        }
      } catch (error: any) {
        console.error('Error deleting user storage files:', error);
        // Not critical if storage deletion fails (folder might not exist)
        // Only log error if it's not a "not found" error
        if (error.code && !['storage/object-not-found', 'storage/unauthorized'].includes(error.code)) {
          errors.push(`storage: ${error.message}`);
        }
      }

      // Delete Firebase Auth account
      // Note: deleteUser requires recent authentication. If it fails, user may need to re-authenticate
      try {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === userId) {
          try {
            await firebaseDeleteUser(currentUser);
            deletedCollections.push('firebaseAuth');
          } catch (authError: any) {
            // Handle specific Firebase Auth errors
            if (authError.code === 'auth/requires-recent-login') {
              errors.push('firebaseAuth: Please sign out and sign back in, then try deleting your account again');
              // Still consider it a success if Firestore data was deleted
              if (deletedCollections.length > 0) {
                return {
                  success: true,
                  message: `Your data has been deleted from our database. Please sign out and sign back in, then try deleting your account again to remove the authentication account, or contact support.`,
                  deletedCollections,
                  errors,
                };
              }
            } else {
              throw authError;
            }
          }
        }
      } catch (error: any) {
        console.error('Error deleting Firebase Auth account:', error);
        errors.push(`firebaseAuth: ${error.message}`);
        
        // If auth deletion fails but Firestore deletion succeeded, still report partial success
        if (deletedCollections.length > 0) {
          return {
            success: true,
            message: `Your data has been deleted from our database. There was an issue deleting the authentication account. Please contact support if you need assistance.`,
            deletedCollections,
            errors,
          };
        }
      }

      // If we have errors but also deleted some data, return partial success
      if (errors.length > 0 && deletedCollections.length > 0) {
        return {
          success: true,
          message: `Account data deleted with some warnings. ${deletedCollections.length} collections cleaned.`,
          deletedCollections,
          errors,
        };
      }

      // Complete success
      if (deletedCollections.length > 0 || errors.length === 0) {
        return {
          success: true,
          message: `Your account and all associated data have been permanently deleted.`,
          deletedCollections,
          errors: errors.length > 0 ? errors : undefined,
        };
      }

      return {
        success: false,
        message: 'No data was found to delete, or deletion failed.',
        deletedCollections: [],
        errors,
      };

    } catch (error: any) {
      console.error('Error in deleteUserData:', error);
      return {
        success: false,
        message: `Failed to delete account: ${error.message}`,
        deletedCollections,
        errors: [error.message],
      };
    }
  },

  /**
   * Request data deletion (for GDPR compliance)
   * This can be used to create a deletion request that is processed later
   */
  async requestDataDeletion(userId: string, userEmail: string, reason?: string): Promise<{ success: boolean; message: string }> {
    try {
      // In a production app, you might want to:
      // 1. Create a deletion request document
      // 2. Send an email notification
      // 3. Process deletion asynchronously
      // 4. Log the request for compliance

      // For now, we'll proceed with immediate deletion
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      const userRole = userData?.role || 'athlete';

      return await this.deleteUserData(userId, userRole);
    } catch (error: any) {
      return {
        success: false,
        message: `Failed to process deletion request: ${error.message}`,
      };
    }
  },
};
